import React, { useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { LuChevronDown } from "react-icons/lu";

export default function Header({ className }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  // Set up the scroll progress tracker over a 300vh scrollable runway
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  // Apply a spring to the scroll progress to get that premium, momentum-based smooth scrubbing feel
  const smoothProgress = useSpring(scrollYProgress, {
    damping: 35,
    stiffness: 90,
    restDelta: 0.001,
  });

  // Keep the background video frame in perfect frame-accurate sync with smooth LERP scroll scrubbing
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Ensure the video is explicitly paused and won't autoplay
    video.pause();
    video.currentTime = 0;

    // State parameters for smooth LERP interpolation and seek management
    let targetProgress = 0;
    let currentProgress = 0;
    const lerpFactor = 0.08; // Buttery smooth inertia coefficient
    let isSeeking = false;
    let pendingSeekTime = null;
    let animationFrameId;

    // Function to safely seek without overwhelming the browser's video decoder
    const performSeek = (time) => {
      if (isNaN(time) || !isFinite(time)) return;
      
      // Keep within video duration bounds
      const maxDuration = video.duration || 7;
      const safeTime = Math.max(0, Math.min(time, maxDuration - 0.05));

      if (isSeeking) {
        // If a seek is already active, queue this newest target time as the next destination
        pendingSeekTime = safeTime;
      } else {
        isSeeking = true;
        video.currentTime = safeTime;
      }
    };

    // Listeners for seek states to execute queued frames instantly once the hardware decoder is free
    const handleSeeking = () => {
      isSeeking = true;
    };

    const handleSeeked = () => {
      isSeeking = false;
      if (pendingSeekTime !== null) {
        const nextTime = pendingSeekTime;
        pendingSeekTime = null;
        performSeek(nextTime);
      }
    };

    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);

    // Force video to stay paused in case of focus or OS media interruptions
    const preventAutoPlay = () => {
      video.pause();
    };
    video.addEventListener("play", preventAutoPlay);

    // The central requestAnimationFrame render loop
    const tick = () => {
      const container = containerRef.current;
      if (!container) {
        animationFrameId = requestAnimationFrame(tick);
        return;
      }

      // Calculate pure viewport-aware scroll progress
      const rect = container.getBoundingClientRect();
      const totalScrollableHeight = container.clientHeight - window.innerHeight;

      if (totalScrollableHeight > 0) {
        // Calculate the absolute progress (0.0 to 1.0) based on sticky container scroll
        const scrolled = -rect.top;
        const progress = Math.max(0, Math.min(scrolled / totalScrollableHeight, 1));
        targetProgress = progress;
      }

      // Linear interpolation (LERP) for fluid deceleration
      currentProgress += (targetProgress - currentProgress) * lerpFactor;

      // Close the gap when values are extremely close to conserve CPU
      if (Math.abs(targetProgress - currentProgress) < 0.0001) {
        currentProgress = targetProgress;
      }

      // Sync the video currentTime with the interpolated progress
      const duration = video.duration || 7;
      const targetTime = currentProgress * duration;

      // Only invoke seek if targetTime is different from current time
      if (Math.abs(video.currentTime - targetTime) > 0.01) {
        performSeek(targetTime);
      }

      animationFrameId = requestAnimationFrame(tick);
    };

    // Kick off the frame-accurate animation loop
    animationFrameId = requestAnimationFrame(tick);

    // Initial metadata load to lock the first frame instantly
    const handleMetadata = () => {
      video.pause();
      video.currentTime = 0;
    };
    video.addEventListener("loadedmetadata", handleMetadata);

    return () => {
      cancelAnimationFrame(animationFrameId);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("play", preventAutoPlay);
      video.removeEventListener("loadedmetadata", handleMetadata);
    };
  }, []);

  // Subtitle fades out quickly
  const subtitleOpacity = useTransform(smoothProgress, [0, 0.15], [1, 0]);
  const subtitleY = useTransform(smoothProgress, [0, 0.15], [0, -25]);

  // Main Title "LAOUNGE" transformation
  const logoScale = useTransform(smoothProgress, [0, 0.35], [1, 0.228]);
  const logoX = useTransform(smoothProgress, [0, 0.35], [0, 0]);
  const logoY = useTransform(smoothProgress, [0, 0.35], [-10, -20]);

  // Header Nav Links & CTA Button fade in
  const navOpacity = useTransform(smoothProgress, [0.22, 0.4], [0, 1]);
  const navY = useTransform(smoothProgress, [0.22, 0.4], [-30, 0]);

  // Glass Box Container fades and slides away to the left
  const boxOpacity = useTransform(smoothProgress, [0, 0.24], [1, 0]);
  const boxX = useTransform(smoothProgress, [0, 0.24], [0, -100]);
  const boxScale = useTransform(smoothProgress, [0, 0.24], [1, 0.92]);

  // Right Paragraph fades and slides away to the right
  const rightParagraphOpacity = useTransform(smoothProgress, [0, 0.24], [1, 0]);
  const rightParagraphX = useTransform(smoothProgress, [0, 0.24], [0, 100]);
  const rightParagraphScale = useTransform(smoothProgress, [0, 0.24], [1, 0.92]);

  // Bottom scroll helper indicator
  const indicatorOpacity = useTransform(smoothProgress, [0, 0.1], [0.7, 0]);

  // Stage 2 transforms
  const secondStageLeftOpacity = useTransform(smoothProgress, [0.52, 0.78], [0, 1]);
  const secondStageLeftX = useTransform(smoothProgress, [0.52, 0.78], [-120, 0]);
  const secondStageRightOpacity = useTransform(smoothProgress, [0.58, 0.86], [0, 1]);
  const secondStageRightX = useTransform(smoothProgress, [0.58, 0.86], [120, 0]);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet" />

      <div
        ref={containerRef}
        className={"relative w-full h-[300vh] bg-neutral-950 select-none " + (className || "")}
      >
        {/* Sticky Frame representing the viewport */}
        <div className="sticky top-0 left-0 w-full h-screen overflow-hidden flex flex-col justify-between">
          
          {/* Background Video Layer */}
          <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
            <video
              ref={videoRef}
              playsInline
              muted
              loop
              preload="auto"
              className="w-full h-full object-cover"
            >
              <source
                src="https://cdn.jiro.build/Laounge%20Coffe/Launge%20Coffe%20Header%20Video.mp4"
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
            {/* Subtle elegant gradient overlay for pristine typography legibility */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/50 z-10" />
          </div>

          {/* Global Floating Header (Always on Top) */}
          <header className="relative z-30 w-full pt-8 pl-20 pr-20 flex justify-between items-start">
            {/* Brand Logo Group */}
            <div className="flex flex-col items-start gap-2">
              <motion.p
                id="hero-subtitle"
                style={{
                  opacity: subtitleOpacity,
                  y: subtitleY,
                  fontSize: "23.065px",
                  fontWeight: 800,
                  alignSelf: "stretch",
                  fontFamily: "Inter, sans-serif",
                }}
                className="text-white font-extrabold tracking-[0.05em] leading-normal uppercase whitespace-nowrap"
              >
                MADE FOR YOU. BUILT FOR MOMENTS.
              </motion.p>
              
              {/* Animating logo - scales and translates smoothly into position */}
              <motion.h1
                id="hero-title"
                style={{
                  scale: logoScale,
                  x: logoX,
                  y: logoY,
                  originX: 0,
                  originY: 0,
                  fontSize: "175px",
                  lineHeight: "175px",
                  fontWeight: 800,
                  alignSelf: "stretch",
                  fontFamily: "Inter, sans-serif",
                }}
                className="text-white font-extrabold tracking-tight select-none uppercase whitespace-nowrap"
              >
                LAOUNGE
              </motion.h1>
            </div>

            {/* Premium Header Menu Items & CTA Button (Revealed dynamically during scroll) */}
            <motion.nav
              id="floating-nav"
              style={{
                opacity: navOpacity,
                y: navY,
                paddingTop: "16px",
                paddingBottom: "16px",
                paddingLeft: "21px",
                paddingRight: "13px",
              }}
              className="hidden md:flex items-center gap-10 bg-black/20 backdrop-blur-md rounded-full border border-white/10"
            >
              <ul className="flex items-center gap-8">
                {["Brews", "Beans", "Experience", "Journal"].map((item, i) => (
                  <li
                    key={item + i}
                    style={{
                      color: "#FFF",
                      fontFamily: "Inter, sans-serif",
                      fontSize: "16px",
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "normal",
                    }}
                    className="hover:text-white/80 transition-colors cursor-pointer capitalize"
                  >
                    {item}
                  </li>
                ))}
              </ul>
              <button
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                className="bg-white text-black px-6 py-2.5 rounded-full hover:bg-white/90 transition-all shadow-lg capitalize"
              >
                Order Now
              </button>
            </motion.nav>
          </header>

          {/* Dynamic Center Stage: Handles the spatial transitions of the landing elements */}
          <div className="relative z-20 w-full flex-grow flex flex-col justify-end pb-16 px-6 sm:px-12 md:px-16 lg:px-24">
            
            {/* Stage 1 Container: Glass box on left, Description paragraph on right */}
            <div className="w-full flex flex-col lg:flex-row justify-between items-end gap-12 relative">
              
              {/* Left Box Component (Glassmorphism) */}
              <motion.section
                id="glass-feature-box"
                style={{
                  opacity: boxOpacity,
                  x: boxX,
                  scale: boxScale,
                  width: "406px",
                  maxWidth: "100%",
                  height: "481px",
                  borderRadius: "12px",
                  padding: "30px",
                  marginTop: "70px",
                }}
                className="flex flex-col justify-between bg-white/10 backdrop-blur-[7.2px] border border-white/15 pointer-events-auto shadow-2xl"
              >
                <div className="flex flex-col gap-6">
                  <h2
                    id="box-large-text"
                    style={{
                      width: "100%",
                      maxWidth: "346px",
                      fontSize: "43.507px",
                      fontWeight: 700,
                      lineHeight: "43.507px",
                      fontFamily: "Inter, sans-serif",
                    }}
                    className="text-white font-bold text-left tracking-tight"
                  >
                    Every detail is designed to make your coffee break feel better
                  </h2>
                </div>

                {/* Dotted/Dashed Divider */}
                <div className="w-full border-t border-dashed border-white/20 my-2" />

                {/* Bottom right text inside the box */}
                <p
                  id="box-bottom-text"
                  style={{
                    width: "100%",
                    maxWidth: "176px",
                    fontSize: "20px",
                    fontWeight: 500,
                    lineHeight: "normal",
                    fontFamily: "Inter, sans-serif",
                  }}
                  className="text-white font-medium text-right self-end opacity-90"
                >
                  Beautiful, durable,
                  <br />
                  and made to elevate
                  <br />
                  every sip.
                </p>
              </motion.section>

              {/* Right Side Paragraph Component */}
              <motion.section
                style={{
                  opacity: rightParagraphOpacity,
                  x: rightParagraphX,
                  scale: rightParagraphScale,
                }}
                className="flex flex-col items-end lg:mb-12 pointer-events-auto"
              >
                <p
                  id="right-side-paragraph"
                  style={{
                    width: "100%",
                    maxWidth: "491px",
                    fontSize: "22px",
                    fontWeight: 500,
                    fontFamily: "Inter, sans-serif",
                  }}
                  className="text-white font-medium text-left lg:text-left leading-normal opacity-90"
                >
                  Every detail is designed to make your coffee break feel better. From the perfect grip to premium insulation, Oryzo brings comfort, style, and warmth together in every cup.
                </p>
              </motion.section>
            </div>

            {/* Stage 2 Container: Revealed during 3s+ scroll progression */}
            <div className="absolute inset-0 flex flex-col lg:flex-row justify-between items-center gap-12 px-6 sm:px-12 md:px-16 lg:px-24 pointer-events-none">
              {/* Left Side Slide-in Text (Aligned with Logo/Title on the left) */}
              <motion.div
                style={{
                  opacity: secondStageLeftOpacity,
                  x: secondStageLeftX,
                }}
                className="w-full lg:w-1/2 flex justify-start pointer-events-auto"
              >
                <h2
                  style={{
                    fontSize: "56.856px",
                    fontWeight: 700,
                    lineHeight: "56.856px",
                    fontFamily: "Inter, sans-serif",
                  }}
                  className="text-white font-bold text-left tracking-tight max-w-[500px]"
                >
                  ISN'T JUST A COFFE FOR YOU !
                </h2>
              </motion.div>

              {/* Right Side Slide-in Text */}
              <motion.div
                style={{
                  opacity: secondStageRightOpacity,
                  x: secondStageRightX,
                }}
                className="w-full lg:w-1/2 flex justify-end pointer-events-auto"
              >
                <p
                  style={{
                    fontSize: "48.286px",
                    fontWeight: 700,
                    lineHeight: "48.286px",
                    fontFamily: "Inter, sans-serif",
                  }}
                  className="text-white font-bold text-left lg:text-right tracking-tight max-w-[550px]"
                >
                  It's a emotion for us and we always try to give our customer to best
                </p>
              </motion.div>
            </div>
          </div>

          {/* Dynamic Scroll Indicator helper */}
          <motion.div
            style={{ opacity: indicatorOpacity }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/50 text-xs font-semibold tracking-widest uppercase pointer-events-none"
          >
            <span>Scroll to Brew</span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            >
              <LuChevronDown className="w-4 h-4" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
}